/**
 * LogoPreviewModal — Large logo preview with SVG/PNG download buttons.
 */

export default function LogoPreviewModal({ svg, name, brandName, onClose }) {
  if (!svg) return null;

  function downloadSvg() {
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${brandName || 'logo'}-${name || 'variant'}.svg`; a.click();
    URL.revokeObjectURL(url);
  }

  function downloadPng(size) {
    const canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      const hasBg = svg.match(/<rect[^>]*fill=["'](?!none|#fff|#FFF|white)[^"']+["']/);
      if (!hasBg) { ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, size, size); }
      const scale = Math.min(size * 0.9 / img.naturalWidth, size * 0.9 / img.naturalHeight);
      const w = img.naturalWidth * scale, h = img.naturalHeight * scale;
      ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
      canvas.toBlob(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `${brandName || 'logo'}-${name || 'variant'}-${size}px.png`; a.click();
        URL.revokeObjectURL(url);
      }, 'image/png');
    };
    img.onerror = () => { alert('PNG export failed. Try downloading the SVG instead.'); };
    // Use Blob URL instead of data URI for better cross-browser support
    const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    img.src = URL.createObjectURL(svgBlob);
  }

  const isTransparent = name === 'transparent' || name === 'Transparent';
  const isDark = svg.includes('fill="#1') || svg.includes('fill="#0') || name?.toLowerCase().includes('dark');

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">{name}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
          </div>

          {/* Large preview */}
          <div className={`rounded-xl p-8 flex items-center justify-center min-h-[400px] ${isDark ? '' : 'border border-gray-200'}`}
            style={isTransparent ? { backgroundImage: 'linear-gradient(45deg, #e0e0e0 25%, transparent 25%), linear-gradient(-45deg, #e0e0e0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e0e0e0 75%), linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)', backgroundSize: '24px 24px', backgroundPosition: '0 0, 0 12px, 12px -12px, -12px 0' } : {}}>
            <div dangerouslySetInnerHTML={{ __html: svg }} className="w-full max-h-[400px] [&>svg]:w-full [&>svg]:max-h-[400px] [&>svg]:mx-auto" />
          </div>

          {/* Download options */}
          <div className="mt-5">
            <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Download</h4>
            <div className="flex flex-wrap gap-2">
              <button onClick={downloadSvg} className="text-xs px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700">SVG (Vector)</button>
              <button onClick={() => downloadPng(512)} className="text-xs px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">PNG 512px</button>
              <button onClick={() => downloadPng(1024)} className="text-xs px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">PNG 1024px</button>
              <button onClick={() => downloadPng(2048)} className="text-xs px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">PNG 2048px</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
